<!-- Colored callout box for informational messages. -->

<script lang="ts" setup>
import type { ClassProp } from '@nzyme/vue-utils/classProp.js';
import type { Component, VNodeChild } from 'vue';

interface Props {
  color?: 'blue' | 'green' | 'red' | 'white' | 'yellow';
  border?: boolean;
  size?: 'large' | 'medium' | 'small';
  icon?: Component;
  contentClass?: ClassProp;
}

interface Slots {
  title: () => VNodeChild;
  default: () => VNodeChild;
  top: () => VNodeChild;
  bottom: () => VNodeChild;
}

defineProps<Props>();
defineSlots<Slots>();
</script>

<template>
  <div
    :class="[css.block, color && css[`color_${color}`], size && css[`size_${size}`], border && css.border]"
    :role="color === 'red' ? 'alert' : undefined"
  >
    <slot name="top" />

    <div :class="css.wrapper">
      <component
        :is="icon"
        v-if="icon"
        :class="css.icon"
      />
      <p
        v-if="$slots.title"
        :class="css.title"
      >
        <slot name="title" />
      </p>
      <p :class="[css.content, contentClass]"><slot /></p>
    </div>

    <slot name="bottom" />
  </div>
</template>

<style lang="scss" module="css">
.block {
  border-radius: $border-radius-10;
  display: flex;
  flex-direction: column;

  --gap: #{$spacing-10};
  gap: var(--gap);

  // Default for info
  --text-color: #{$color-text};
  --background: #{$color-white};
  --icon-color: #{$color-doctor-600};
  --border-color: #{$color-gray-200};

  background: var(--background);
  border-color: var(--border-color);

  // Default for medium
  padding: $spacing-16;
  --icon-size: 24px;
  --text-size: #{$font-size-14};
}

.size {
  &_small {
    padding: $spacing-10;
    --text-size: #{$font-size-12};
    --gap: #{$spacing-08};
    --icon-size: 16px;
  }

  &_large {
    padding: $spacing-16;
    --text-size: #{$font-size-16};
    --gap: #{$spacing-12};
    --icon-size: 24px;
  }
}

.color {
  &_white {
    --background: #{$color-white};
    --icon-color: #{$color-doctor-600};
    --border-color: #{$color-doctor-200};
  }

  &_blue {
    --background: #{$color-doctor-50};
    --icon-color: #{$color-doctor-600};
    --border-color: #{$color-doctor-200};
  }

  &_red {
    --text-color: #{$color-patient-600};
    --background: #{$color-patient-50};
    --icon-color: #{$color-patient-600};
    --border-color: #{$color-patient-300};
  }

  &_green {
    --text-color: #{$color-green-500};
    --background: #{$color-green-100};
    --icon-color: #{$color-green-500};
    --border-color: #{$color-green-200};
  }

  &_yellow {
    --background: #{$color-yellow-25};
    --icon-color: #{$color-yellow-700};
    --border-color: #{$color-yellow-200};
  }
}

.border {
  border-width: 1px;
  border-style: solid;
}

.wrapper {
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: $spacing-02;
  align-items: center;
  font-size: var(--text-size);
  font-weight: $font-weight-500;
  line-height: 1.5;
  color: var(--text-color);
}

.icon {
  width: var(--icon-size);
  margin-right: var(--gap);
  flex: 0 0 auto;
  color: var(--icon-color);
  align-self: flex-start;
  grid-row: 1;
  grid-column: 1;
}

.title {
  font-weight: $font-weight-600;
  font-size: 1.1em;
  grid-column: 2;
}

.content {
  grid-column: 2;
}
</style>
